/*
 * quote-button-safety.js
 *
 * Fixes the intermittent "I click Request a Formal Quote and nothing
 * happens" bug on product pages.
 *
 * Root cause: the visible quote button is an <a href="#"> (the theme's
 * button_custom block, #quote-button-wrapper) whose open-the-quote-form
 * behaviour is attached by Quotify's app JavaScript, which loads
 * ASYNCHRONOUSLY from Quotify's servers. If a customer clicks before that
 * script has bound its handler, the dead "#" link does nothing and the
 * button looks broken. Reloading "fixes" it only because the script has
 * finished by then.
 *
 * The fix: suppress the dead "#" navigation, let Quotify's own handler
 * take the click first, and — only if the form hasn't opened a moment
 * later — dispatch a real pointer/mouse event sequence to the button,
 * retrying on a short interval until the form actually opens (or a
 * timeout). A dispatched sequence is a harmless no-op until Quotify is
 * ready and instantly opens the form once it is, so a click is never lost,
 * whether Quotify loaded in 200ms or 6 seconds.
 *
 * Safe by design:
 *   - Pure fallback: if Quotify's own handler already opened the form, we
 *     detect it and do nothing — so a working click is never double-fired.
 *   - Only acts on the quote button (by wrapper id or exact button text).
 *   - Won't re-enter itself: synthetic events set an in-flight guard and
 *     still get their dead "#" navigation cancelled.
 *   - Ignores the legacy .formalquote2 links (handled elsewhere).
 */
(function () {
  'use strict';

  var MAX_TRIES = 25;      // ~7.5s of retries at RETRY_MS spacing
  var RETRY_MS = 300;
  var FIRST_DELAY_MS = 400; // give Quotify's own handler a head start first
  var syntheticInFlight = false;
  var loopActive = false;

  // Is this element the visible "Request a Formal Quote" trigger?
  function isQuoteTrigger(el) {
    if (!el || !el.closest) return false;
    // Never treat the legacy Volusion links as our button.
    if (el.closest('.formalquote2link, .formalquote2')) return false;
    // The theme's styled button lives inside #quote-button-wrapper.
    if (el.closest('#quote-button-wrapper')) return true;
    // Fallback: match Quotify's app-block button by visible text, in case
    // the styled wrapper isn't the bound element.
    var btn = el.closest('button, a.button, a.btn, .btn, [role="button"]');
    if (!btn) return false;
    var text = (btn.textContent || '').trim().toLowerCase();
    return text === 'request a formal quote' || text === 'request quote';
  }

  // Resolve the actual clickable element we should dispatch events to.
  function resolveButton(el) {
    var wrapper = el.closest('#quote-button-wrapper');
    if (wrapper) {
      return wrapper.querySelector('a, button') || wrapper;
    }
    return el.closest('button, a.button, a.btn, .btn, [role="button"]') || el;
  }

  // The Quotify quote form lives in an iframe (#quotify-frame) that is
  // injected when the form opens (see gclid-quotify-injector.js). Its
  // presence + visibility is our "the form actually opened" signal.
  function quoteFormOpen() {
    var f = document.getElementById('quotify-frame');
    if (f) {
      var r = f.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return true;
    }
    // Be tolerant of Quotify markup changes: any visible quotify overlay.
    var alt = document.querySelector(
      'iframe[src*="quotify"], [class*="quotify-modal"], [id*="quotify-modal"]'
    );
    if (alt) {
      var ar = alt.getBoundingClientRect();
      if (ar.width > 0 && ar.height > 0) return true;
    }
    return false;
  }

  function fireSequence(el) {
    syntheticInFlight = true;
    var events = ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'];
    for (var i = 0; i < events.length; i++) {
      try {
        el.dispatchEvent(
          new MouseEvent(events[i], { bubbles: true, cancelable: true, view: window })
        );
      } catch (_) {}
    }
    // Release the guard after the current event loop tick so our own
    // synthetic click doesn't re-enter the capture handler below.
    setTimeout(function () { syntheticInFlight = false; }, 0);
  }

  // Fallback retry loop. We wait FIRST_DELAY_MS before the first attempt so
  // that if Quotify's own handler already opened the form on the real click,
  // we detect it and never fire — avoiding any double-open / toggle-closed.
  function scheduleFallback(button) {
    if (loopActive) return; // a retry loop is already running
    loopActive = true;

    var tries = 0;
    function check() {
      if (quoteFormOpen()) { loopActive = false; return; } // already open — done
      if (tries >= MAX_TRIES) { loopActive = false; return; } // give up quietly
      tries++;
      fireSequence(button);
      setTimeout(check, RETRY_MS);
    }
    setTimeout(check, FIRST_DELAY_MS);
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (!isQuoteTrigger(target)) return;

      // Kill the dead "#" jump on every quote-button click — real OR our own
      // synthetic ones. (preventDefault does NOT stop Quotify's own click
      // listener; it only cancels the anchor's hash navigation.)
      event.preventDefault();

      // Our own dispatched events: let them reach Quotify, don't reschedule.
      if (syntheticInFlight) return;

      // Real user click: don't stop propagation — Quotify's handler still
      // gets it. We only step in as a fallback if the form doesn't open.
      scheduleFallback(resolveButton(target));
    },
    true // capture phase — cancel the anchor's default before it navigates
  );
})();
